import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { designSystem } from '@yola/ws-ui';
import { i18next, integration, customHtml } from '@yola/ws-sdk';
import customCodeTriggerIds from 'src/js/modules/common/constants/custom-code-trigger-ids';
import segment from 'src/js/modules/analytics/segment';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import CodeSnippetListField from './code-snippet-list-field';

const { Stack, Paragraph, CtaBanner } = designSystem;
const {
  trackers: { trackUpgradeTriggerClicked },
} = segment;
const { fieldNames } = customHtml.constants;

const codeGroups = {
  [fieldNames.HEAD_CODE]: 'inside-head-tag',
  [fieldNames.HEADER_CODE]: 'after-body-tag',
  [fieldNames.FOOTER_CODE]: 'before-body-tag',
};

const CustomCodeSection = ({ triggerId, dialogType, control, captions, showSettingsDialog }) => {
  const upsells = useSelector(integration.selectors.getUpsells);
  const limits = getCurrentPackageFeatureLimits();
  const isAvailable = limits.customCode.available;

  const onUpgrade = () => {
    trackUpgradeTriggerClicked(triggerId);

    return upsells.customCode
      .onUpgrade({
        actionId: triggerId,
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
      });
  };

  return (
    <Stack gap="spacing-m">
      {!isAvailable && (
        <CtaBanner
          title={i18next.t('Gain access to custom code editing by upgrading your subscription!')}
          iconGlyph="upgrade"
          animate
          ctaButton={{
            label: i18next.t('Upgrade now'),
            onClick: onUpgrade,
          }}
        />
      )}
      <Paragraph appearance="medium-emphasis" size="small">
        {captions.description}
      </Paragraph>

      <CodeSnippetListField
        title={i18next.t('Inside <head> tag')}
        control={control}
        fieldName={fieldNames.HEAD_CODE}
        triggerId={triggerId}
        dialogType={dialogType}
        codeGroup={codeGroups[fieldNames.HEAD_CODE]}
        isAvailable={isAvailable}
        showSettingsDialog={showSettingsDialog}
      />

      <CodeSnippetListField
        title={i18next.t('After <body> tag')}
        control={control}
        fieldName={fieldNames.HEADER_CODE}
        triggerId={triggerId}
        dialogType={dialogType}
        codeGroup={codeGroups[fieldNames.HEADER_CODE]}
        isAvailable={isAvailable}
        showSettingsDialog={showSettingsDialog}
      />

      <CodeSnippetListField
        title={i18next.t('Before </body> tag')}
        control={control}
        fieldName={fieldNames.FOOTER_CODE}
        triggerId={triggerId}
        dialogType={dialogType}
        codeGroup={codeGroups[fieldNames.FOOTER_CODE]}
        isAvailable={isAvailable}
        showSettingsDialog={showSettingsDialog}
      />
    </Stack>
  );
};

CustomCodeSection.propTypes = {
  triggerId: PropTypes.oneOf(Object.values(customCodeTriggerIds)).isRequired,
  dialogType: PropTypes.string.isRequired,
  control: PropTypes.shape().isRequired,
  captions: PropTypes.shape({
    description: PropTypes.string.isRequired,
  }).isRequired,
  showSettingsDialog: PropTypes.func.isRequired,
};

export default CustomCodeSection;
